// src/pages/SLASettings.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { db } from "../firebase/config";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../Context/AuthContext";
import { toast } from "react-toastify";

import "../styles/SLASettings.css";

const PRIORITIES = [
  { key: "critical", label: "CRITICAL" },
  { key: "high", label: "HIGH" },
  { key: "medium", label: "MEDIUM" },
  { key: "low", label: "LOW" },
];

const PAUSE_STATUS = "Waiting on Customer";

export default function SLASettings({ onClose, onSaved }) {
  const { user } = useAuth();
  const companyId = user?.companyId;

  const slaRef = useMemo(
    () =>
      companyId ? doc(db, `companies/${companyId}/settings/slaPolicy`) : null,
    [companyId]
  );
  const companyRef = useMemo(
    () => (companyId ? doc(db, "companies", companyId) : null),
    [companyId]
  );

  const [companyTz, setCompanyTz] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    warnMinutesBeforeBreach: 30,
    pauseWhenWaitingOnCustomer: true,
    doneStatuses: ["Resolved", "Closed"],
    timeZone: "",
    targets: {
      critical: { firstResponseMins: 60, resolutionMins: 240 },
      high: { firstResponseMins: 120, resolutionMins: 480 },
      medium: { firstResponseMins: 240, resolutionMins: 1440 },
      low: { firstResponseMins: 480, resolutionMins: 4320 },
    },
  });

  // NEW: UI feedback state
  const [justSaved, setJustSaved] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Load company TZ first
  useEffect(() => {
    (async () => {
      if (!companyRef) return;
      try {
        const snap = await getDoc(companyRef);
        const tz = snap.data()?.timeZone || "";
        setCompanyTz(tz);
      } catch (e) {
        console.error("Failed to load company time zone", e);
      }
    })();
  }, [companyRef]);

  // Load SLA doc
  useEffect(() => {
    (async () => {
      if (!slaRef) return;
      try {
        const snap = await getDoc(slaRef);
        if (snap.exists()) {
          const data = snap.data();
          setForm((prev) => ({
            ...prev,
            ...data,
            timeZone: companyTz || data.timeZone || prev.timeZone || "Etc/UTC",
            targets: { ...prev.targets, ...(data?.targets || {}) },
          }));
        } else {
          setForm((prev) => ({
            ...prev,
            timeZone: companyTz || prev.timeZone || "Etc/UTC",
          }));
        }
      } catch (e) {
        console.error("Failed to load SLA policy", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [slaRef, companyTz]);

  const setTarget = useCallback((pKey, field, value) => {
    const num = Number(value);
    setForm((f) => ({
      ...f,
      targets: {
        ...f.targets,
        [pKey]: { ...f.targets[pKey], [field]: Number.isNaN(num) ? 0 : num },
      },
    }));
  }, []);

  // Save handler with visual feedback
  const save = useCallback(async () => {
    if (!slaRef || saving) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        pauseStatus: PAUSE_STATUS,
        timeZone: companyTz || form.timeZone || "Etc/UTC",
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
        updatedByEmail: user?.email || null,
      };

      await setDoc(slaRef, payload, { merge: true });

      setJustSaved(true);
      setLastSavedAt(new Date());

      if (onSaved) onSaved(payload);

      toast.success("SLA settings updated.");

      setTimeout(() => setJustSaved(false), 2000);
    } catch (e) {
      console.error("Failed to save SLA policy", e);
      toast.error("Could not save SLA settings. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [slaRef, saving, form, companyTz, user, onSaved]);

  // Submit on Cmd/Ctrl + Enter
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [save]);

  const timeZoneText = useMemo(
    () =>
      companyTz
        ? `Time Zone: ${companyTz} (edit in Settings → Language & Timezone)`
        : "",
    [companyTz]
  );

  if (loading) return <div className="sla-settings-loading">Loading…</div>;

  return (
    <div className="sla-settings modern">
      {/* Header */}
      <div className="sla-head">
        <div>
          <h2>Simple SLA</h2>
          {lastSavedAt && (
            <p className="sla-last-saved">
              Last saved{" "}
              {lastSavedAt.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>

        <div className="sla-head-right">
          {justSaved && <span className="sla-saved-pill">Saved</span>}
          <div className="kbd-hint">⌘/Ctrl + Enter to Save</div>
        </div>
      </div>

      {/* Grid */}
      <div className="sla-grid">
        {PRIORITIES.map(({ key, label }) => (
          <section className={`priority-card ${key}`} key={key}>
            <header>
              <span className="dot" aria-hidden />
              <h3>{label}</h3>
            </header>

            <div className="row">
              <label>
                <span>First Response</span>
                <div className="input-suffix">
                  <input
                    type="number"
                    min={0}
                    value={form.targets[key].firstResponseMins}
                    onChange={(e) =>
                      setTarget(key, "firstResponseMins", e.target.value)
                    }
                  />
                  <span className="suffix">mins</span>
                </div>
              </label>

              <label>
                <span>Resolution</span>
                <div className="input-suffix">
                  <input
                    type="number"
                    min={0}
                    value={form.targets[key].resolutionMins}
                    onChange={(e) =>
                      setTarget(key, "resolutionMins", e.target.value)
                    }
                  />
                  <span className="suffix">mins</span>
                </div>
              </label>
            </div>
          </section>
        ))}
      </div>

      {/* Options */}
      <div className="options">
        <div className="opt-row two-cols">
          <label className="stack">
            <span>Warn before breach</span>
            <div className="input-suffix">
              <input
                type="number"
                min={0}
                value={form.warnMinutesBeforeBreach}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    warnMinutesBeforeBreach: Number(e.target.value || 0),
                  }))
                }
              />
              <span className="suffix">mins</span>
            </div>
          </label>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.pauseWhenWaitingOnCustomer}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  pauseWhenWaitingOnCustomer: e.target.checked,
                }))
              }
            />
            Pause timers when status is <strong>“{PAUSE_STATUS}”</strong>
          </label>
        </div>

        {companyTz && <p className="timezone muted">{timeZoneText}</p>}
      </div>

      {/* Sticky Footer */}
      <div className="sla-actions">
        {onClose && (
          <button className="ghost" type="button" onClick={onClose}>
            Cancel
          </button>
        )}
        <button
          className={`primary ${saving ? "loading" : ""} ${
            justSaved ? "saved" : ""
          }`}
          type="button"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
